import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { DateTime } from 'luxon';
import {
    Box,
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    FormControlLabel,
    Grid,
    MenuItem,
    TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import AutoSearch from 'components/common/AutoSearch';
import { ModalActionProps } from 'models/AutoModals';
import { InvoiceModel } from 'models/InvoiceModel';
import { CommonInvoiceableList, CommonInvoiceableTable } from 'modules/invoices/common/InvoiceForm';
import Api from 'utils/api';
import { removeItemAtIndex, replaceItemAtIndex } from 'utils/common';


type SimpleCreationDialogProps = {} & ModalActionProps<{
    payer_type: string,
    payer_id: number,
    created_at: string,
    due_date: string,
    items: any[],
    note: string,
    tax: number,
}>

const SimpleCreationDialog = ({onFinish}: SimpleCreationDialogProps) => {
    const {t} = useTranslation();
    const [payer, setPayer] = useState<Partial<InvoiceModel['payer']>>({
        type: 'shipper',
    });
    const [created, setCreated] = useState(DateTime.now());
    const [due, setDue] = useState(DateTime.now().plus({days: 7}));
    const [items, setItems] = useState([{
        name: '',
        description: '',
        quantity: 1,
        price: '',
    }]);
    const [note, setNote] = useState('');
    const [tax, setTax] = useState(0);

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!payer.type || !payer.id || !due || !items.length)
            return;

        onFinish({
            payer_type: payer.type,
            payer_id: payer.id,
            created_at: created.toISODate()!,
            due_date: due.toISODate()!,
            items,
            note,
            tax,
        });
    };

    const search = payer.type === 'shipper' ? Api.company.search : Api.parties.search;

    const Items = isMobile ?
        CommonInvoiceableList :
        CommonInvoiceableTable;

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                <Grid container columnSpacing={2} alignItems="flex-end">
                    <Grid item xs={6} sm={3}>
                        <TextField
                            select
                            id="payerType"
                            label={t('payer_type')}
                            fullWidth
                            margin="normal"
                            variant="outlined"
                            value={payer.type}
                            onChange={e => setPayer({type: e.target.value})}
                        >
                            <MenuItem value="shipper">{t('shipper')}</MenuItem>
                            <MenuItem value="consignee">{t('consignee')}</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <AutoSearch
                            key={payer.type}
                            search={search}
                            onChange={val => val && setPayer(old => ({...old, id: val.id}))}
                            margin="normal"
                            label={t('payer')}
                            keepValue
                        />
                    </Grid>

                    <Grid item xs={6} sm={3}>
                        <DatePicker
                            label={t('issue_date')}
                            format="yyyy-MM-dd"
                            slotProps={{textField: {fullWidth: true, required: true}}}
                            onChange={d => setCreated(d!)}
                            value={created}
                        />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <DatePicker
                            label={t('due_date')}
                            format="yyyy-MM-dd"
                            slotProps={{textField: {fullWidth: true, required: true}}}
                            onChange={d => setDue(d!)}
                            value={due}
                        />
                    </Grid>
                </Grid>

                <Items
                    items={items}
                    add={() => setItems(old => [
                        ...old,
                        {
                            name: '',
                            description: '',
                            quantity: 1,
                            price: '',
                        }
                    ])}
                    remove={i => setItems(old => removeItemAtIndex(old, i))}
                    setVal={(i, name, value) => setItems(old => replaceItemAtIndex(old, i, {...old[i], [name]: value}))}
                />

                <TextField
                    id="note"
                    label={t('note')}
                    fullWidth
                    multiline
                    rows={4}
                    margin="normal"
                    variant="outlined"
                    value={note}
                    onChange={e => setNote(e.target.value)}
                />
            </DialogContent>

            <DialogActions>
                <Button type="submit">{t('add')}</Button>
            </DialogActions>
        </Box>
    );
};

export default SimpleCreationDialog;
